/* @flow */
import React, {useState, useEffect} from 'react';
import {useApolloClient, useLazyQuery} from '@apollo/react-hooks';
import {connect} from 'react-redux';
import {pick} from 'lodash';

import {filteredSearchRequestCountQuery} from '../../queries/filteredCountSearchRequestQuery';
import {ResultsParams} from '../../../server/graphql/params';

import type {Element} from 'react';
import type {State as ReduxState} from '../../reducers/index';
import FiltersContainer from '../Filters/FiltersContainer';
import {searchRequestQueryVariables} from '../../queries/searchRequestQuery';

import type {FilterGroupType} from '@homeaway/react-search-filters';

export type CallbackProps = {
    filterGroups: FilterGroupType[],
    resultsCount?: ?number
};

type StateProps = {
    abacus: $PropertyType<ReduxState, 'abacus'>,
    bounds: $PropertyType<$PropertyType<ReduxState, 'map'>, 'bounds'>,
    expandedSearchHasModifiedMapViewport: $PropertyType<
        $PropertyType<ReduxState, 'expandedGroups'>,
        'hasModifiedMapViewport'
    >,
    inputParams: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'inputParams'
    >,
    inputParamsAreChanged: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'inputParamsAreChanged'
    >,
    inputParamsAreUpdating: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'inputParamsAreUpdating'
    >,
    params: $PropertyType<$PropertyType<ReduxState, 'searchParams'>, 'params'>,
    pathname: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'pathname'
    >,
    system: $PropertyType<ReduxState, 'system'>
};

type CallbackElementType = typeof FiltersContainer;

type OwnProps = {
    children: (callbackProps: CallbackProps) => Element<CallbackElementType>,
    searchResultsLoading: boolean
};

type Props = OwnProps &
    StateProps & {
        lazyLoadCounts: boolean
    };

const noData = {results: {filterGroups: [], resultCount: 0}};

const makeSearchVariables = (
    props: Props,
    overrideParams?: $PropertyType<StateProps, 'params'>
) => {
    const params = overrideParams ? overrideParams : props.params;
    const isOverridingForFilteredSearchRequestCountQuery = !!overrideParams;

    return searchRequestQueryVariables(
        params,
        props.abacus,
        props.system,
        isOverridingForFilteredSearchRequestCountQuery
    );
};

/**
 * Builds GraphQL config for searchRequestQuery
 * @param {Props} props
 */
export const searchRequestQueryConfig = (props: Props) => {
    return {
        query: filteredSearchRequestCountQuery,
        variables: makeSearchVariables(props)
    };
};

/**
 * Builds GraphQL config for filteredSearchRequestCountQuery
 * @param {Props} props
 */
export const filterCountQueryConfig = (props: Props) => {
    const {
        bounds,
        expandedSearchHasModifiedMapViewport,
        inputParams,
        params,
        system: {searchFilterCounts}
    } = props;

    let overrideParams = {
        ...pick({...params, ...inputParams}, ResultsParams),
        include: [],
        pageSize: 0
    };

    /* If expanded search has modified the map viewport (expaned search groups has been uncollapsed)
    then we need update the bounding box to include the expanded searches for the filter count. Note here
    we do not use the bounds in the searchParams but from the transative map state */
    if (expandedSearchHasModifiedMapViewport) {
        overrideParams = {...overrideParams, ...bounds};
    }

    return {
        variables: {
            ...makeSearchVariables(props, overrideParams),
            filterCounts: searchFilterCounts
        }
    };
};

export const FilteredCountDataProvider = (props: Props) => {
    const {
        children,
        searchResultsLoading,
        inputParamsAreChanged,
        inputParamsAreUpdating
    } = props;
    const client = useApolloClient();
    const [filterCountDataCached, setFilterCountDataCached] = useState(noData);
    const [getFilteredCountData, {data: filteredCountData}] = useLazyQuery(
        filteredSearchRequestCountQuery
    );

    useEffect(() => {
        // Make sure we are not trying to update the state while a search is happening
        /* istanbul ignore else */
        if (!searchResultsLoading && !inputParamsAreUpdating) {
            // Filters selection changed
            if (inputParamsAreChanged) {
                getFilteredCountData(filterCountQueryConfig(props));
            } else {
                // Search was performed and a updated filterGroups key might be available in ApolloCache
                // client.readQuery throws an error if no cache found
                try {
                    const searchData = client.readQuery(
                        searchRequestQueryConfig(props)
                    );

                    // Prevent setting the state with null results
                    if (searchData && searchData.results) {
                        setFilterCountDataCached(searchData);
                    }
                } catch (err) {
                    // cache not available after finished loading - run filter search again in an attempt to fetch filter groups
                    getFilteredCountData(filterCountQueryConfig(props));
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        client,
        searchResultsLoading,
        inputParamsAreUpdating,
        inputParamsAreChanged
    ]);

    useEffect(() => {
        // This prevents null results from breaking this component
        /* istanbul ignore else */
        if (filteredCountData && filteredCountData.results) {
            setFilterCountDataCached(filteredCountData);
        }
    }, [filteredCountData]);

    const {
        results: {filterGroups, resultCount}
    } = filterCountDataCached;

    return children({
        resultsCount: resultCount,
        filterGroups
    });
};

FilteredCountDataProvider.displayName = 'FilteredCountDataProvider';

const mapStateToProps = ({
    abacus,
    expandedGroups: {
        hasModifiedMapViewport: expandedSearchHasModifiedMapViewport
    },
    map: {bounds},
    searchParams: {
        pathname,
        params,
        inputParams,
        inputParamsAreChanged,
        inputParamsAreUpdating
    },
    system
}: ReduxState): StateProps => ({
    abacus,
    bounds,
    expandedSearchHasModifiedMapViewport,
    params,
    inputParams,
    inputParamsAreChanged,
    inputParamsAreUpdating,
    pathname,
    system
});

export const ConnectedFilteredCountDataProvider = connect(
    mapStateToProps,
    {}
)(FilteredCountDataProvider);

ConnectedFilteredCountDataProvider.displayName =
    'ConnectedFilteredCountDataProvider';

const ExperimentFilteredCountDataProvider = (props: OwnProps) => {
    return <ConnectedFilteredCountDataProvider {...props} />;
};

export default ExperimentFilteredCountDataProvider;
